import api from "../api";
import { asArray } from "../payloadGuards";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

export interface Integration {
    id?: string;
    name: string;
    practiceId: string;
    connectionStatus: string;
    integrationType: string;
    integrationName?: string;
    integrationDescription?: string;
    username?: string;
    password?: string;
    api_key?: string;
    api_secret?: string;
    api_token?: string;
    api_url?: string;
    api_version?: string;
}

export interface IntegrationPlatform {
    _id: string;
    name: string;
    type_name: string;
    desc: string;
    integration_type: string;
    isActive: boolean;
    inDevelopment: boolean;
    created_at: number;
}

export interface IntegrationState {
    integrations: Integration[];
    selectedIntegration: Integration | null;
    isLoading: boolean;
    integration: Integration | null;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
    platforms: IntegrationPlatform[];
    platformsLoading: boolean;
    platformsError: string | null;
}

export const fetchIntegrations = createAsyncThunk(
    'integration/fetchIntegrations',
    async () => {
        try {
            const response = await api.get('/integrations');
            return response.data.body;
        } catch (error) {
            console.error('Error fetching integrations: ', error);
            throw error;
        }
    }
);

export const fetchIntegrationById = createAsyncThunk(
    'integration/fetchIntegrationById',
    async (id: string) => {
        try {
            const response = await api.get(`/integrations/${id}`);
            return response.data.body;
        } catch (error) {
            console.error('Error fetching integration by ID: ', error);
            throw error;
        }
    }
);

export const fetchIntegrationPlatforms = createAsyncThunk(
    'integration/fetchIntegrationPlatforms',
    async () => {
        try {
            const response = await api.get('/integration-platform');
            return response.data?.body || [];
        } catch (error) {
            console.error('Error fetching integration platforms: ', error);
            throw error;
        }
    }
);

// API Gateway Lambdas can return HTTP 200 with an error payload — surface those as failures
const assertSuccessPayload = (data: any, fallbackMessage: string) => {
    if (data && (Number(data.statusCode) >= 400 || data.error)) {
        throw new Error(data.msg || data.message || data.error || fallbackMessage);
    }
};

export const createIntegration = createAsyncThunk(
    'integration/createIntegration',
    async (integration: Integration) => {
        try {
            const response = await api.post('/scraper/simplepractice', integration);
            assertSuccessPayload(response.data, 'Failed to create integration');
            return response.data;
        } catch (error) {
            console.error('Error creating integration: ', error);
            throw error;
        }
    }
);

export const updateIntegration = createAsyncThunk(
    'integration/updateIntegration',
    async (integration: Integration) => {
        try {
            const response = await api.put(`/scraper/simplepractice/${integration.id}`, integration);
            assertSuccessPayload(response.data, 'Failed to update integration');
            return response.data;
        } catch (error) {
            console.error('Error updating integration: ', error);
            throw error;
        }
    }
);

export const deleteIntegration = createAsyncThunk(
    'integration/deleteIntegration',
    async (id: string) => {
        const response = await api.delete(`/integrations/${id}`);
        return response.data;
    }
);

export const fetchIntegrationByPracticeId = createAsyncThunk(
    'integration/fetchIntegrationByPracticeId',
    async (practiceId: string) => {
        try {
            const response = await api.get(`/integrations/practice/${practiceId}`);

            // Handle different response structures
            const data = response.data?.body || response.data;

            // If data is null, undefined, empty object, or doesn't have an ID, return null
            if (!data || typeof data !== 'object' || Object.keys(data).length === 0 || (!data.id && !data._id)) {
                return null;
            }

            // Normalize the data to use 'id' (as a string) instead of '_id'
            const normalizedData = {
                ...data,
                id: normalizeId(data),
                createdAt: data.createdAt || data.created_at,
                updatedAt: data.updatedAt || data.updated_at
            };

            return normalizedData;
        } catch (error: unknown) {
            console.error('Error fetching integration by practice ID: ', error);

            // If it's a 404 or 500 error (no integration found), return null instead of throwing
            // This prevents the error from showing in the UI when there's simply no integration yet
            if (error && typeof error === 'object' && 'response' in error) {
                const axiosError = error as { response?: { status?: number } };
                if (axiosError.response && (axiosError.response.status === 404 || axiosError.response.status === 500)) {
                    return null;
                }
            }

            // For other errors, throw them
            throw error;
        }
    }
);

// export const fetchConnection = createAsyncThunk(

const initialState: IntegrationState = {
    integrations: [],
    status: 'idle',
    error: null,
    integration: null,
    selectedIntegration: null,
    isLoading: false,
    platforms: [],
    platformsLoading: false,
    platformsError: null,
};

const normalizeId = (data: any): string => {
    if (!data) return '';
    let val = data.id;
    // If id is missing or is an object (which causes [object Object]), try _id
    if (!val || typeof val === 'object') {
        if (data._id) val = data._id;
    }
    // If still an object, try safe stringify, or fallback to empty
    if (typeof val === 'object') {
        return String(val || '');
    }
    return String(val || '');
};

const integrationSlice = createSlice({
    name: 'integration',
    initialState,
    reducers: {
        setIntegrations: (state, action) => {
            state.integrations = asArray(action.payload);
        },
        clearIntegration: (state) => {
            state.integration = null;
        }
    },
    extraReducers: (builder) => {
        // fetchIntegrations
        builder
            .addCase(fetchIntegrations.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(fetchIntegrations.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.isLoading = false;
                // Normalize integration IDs to ensure they are always strings
                state.integrations = asArray<any>(action.payload).map((item: any) => ({
                    ...item,
                    id: normalizeId(item),
                }));
                state.error = null;
            })
            .addCase(fetchIntegrations.rejected, (state, action) => {
                state.status = 'failed';
                state.isLoading = false;
                state.error = action.error.message || 'Failed to fetch integrations';
            })
            // fetchIntegrationById
            .addCase(fetchIntegrationById.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(fetchIntegrationById.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.isLoading = false;
                // Normalize the ID
                const normalizedData = action.payload ? {
                    ...action.payload,
                    id: normalizeId(action.payload)
                } : null;
                state.integration = normalizedData;
                state.error = null;
            })
            .addCase(fetchIntegrationById.rejected, (state, action) => {
                state.status = 'failed';
                state.isLoading = false;
                state.error = action.error.message || 'Failed to fetch integration';
            })
            // fetchIntegrationByPracticeId
            .addCase(fetchIntegrationByPracticeId.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(fetchIntegrationByPracticeId.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.isLoading = false;

                // Normalization happens in the thunk already, but we can double check here
                if (action.payload && (action.payload.id || action.payload._id)) {
                    const normalized = {
                        ...action.payload,
                        id: normalizeId(action.payload)
                    };
                    state.integration = normalized;
                } else {
                    state.integration = null;
                }
                state.error = null;
            })
            .addCase(fetchIntegrationByPracticeId.rejected, (state, action) => {
                state.status = 'failed';
                state.isLoading = false;
                state.integration = null; // Clear integration on error
                state.error = action.error.message || 'Failed to fetch integration by practice ID';
            })
            // createIntegration
            .addCase(createIntegration.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(createIntegration.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.isLoading = false;
                // Normalize the ID
                const normalizedItem = {
                    ...action.payload,
                    id: normalizeId(action.payload)
                };
                state.integrations.push(normalizedItem);
                state.integration = normalizedItem; // Update selected integration
                state.error = null;
            })
            .addCase(createIntegration.rejected, (state, action) => {
                state.status = 'failed';
                state.isLoading = false;
                state.error = action.error.message || 'Failed to create integration';
            })
            // updateIntegration
            .addCase(updateIntegration.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(updateIntegration.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.isLoading = false;
                // Normalize the ID
                const normalizedItem = {
                    ...action.payload,
                    id: normalizeId(action.payload)
                };
                const index = state.integrations.findIndex(integration => integration.id === normalizedItem.id);
                if (index !== -1) {
                    state.integrations[index] = normalizedItem;
                }
                state.integration = normalizedItem; // Update selected integration
                state.error = null;
            })
            .addCase(updateIntegration.rejected, (state, action) => {
                state.status = 'failed';
                state.isLoading = false;
                state.error = action.error.message || 'Failed to update integration';
            })
            // deleteIntegration
            .addCase(deleteIntegration.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(deleteIntegration.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.isLoading = false;
                state.integrations = state.integrations.filter(integration => integration.id !== action.payload.id);
                state.error = null;
            })
            .addCase(deleteIntegration.rejected, (state, action) => {
                state.status = 'failed';
                state.isLoading = false;
                state.error = action.error.message || 'Failed to delete integration';
            })
            // fetchIntegrationPlatforms
            .addCase(fetchIntegrationPlatforms.pending, (state) => {
                state.platformsLoading = true;
                state.platformsError = null;
            })
            .addCase(fetchIntegrationPlatforms.fulfilled, (state, action) => {
                state.platformsLoading = false;
                state.platforms = action.payload || [];
            })
            .addCase(fetchIntegrationPlatforms.rejected, (state, action) => {
                state.platformsLoading = false;
                state.platformsError = action.error.message || 'Failed to fetch integration platforms';
            });
    }
});

export const { reducer: integrationReducer, actions: integrationActions } = integrationSlice;
export default integrationReducer;
